import React, { useState, useEffect, useRef } from "react";

const getTeamLogo = (teamName) => {
  if (!teamName) return "/images/logos/ringette_live_logo.png";

  switch (teamName.toLowerCase()) {
    case "wora":
      return "/images/logos/wora.png";
    case "cora":
      return "/images/logos/cora.png";
    // Add more cases here as needed
    default:
      return "/images/logos/ringette_live_logo.png";
  }
};

const Scoreboard = () => {
  // Use refs to store data to avoid render triggers
  const dataRef = useRef({
    homeTeamName: "Home",
    awayTeamName: "Away",
    homeScore: 0,
    awayScore: 0,
    period: 1,
    periodMinute: 0,
    periodSecond: 0,
    reason: "",
  });

  // State only for UI elements that need to trigger renders
  const [homeTeamNameUI, setHomeTeamNameUI] = useState(
    dataRef.current.homeTeamName
  );
  const [awayTeamNameUI, setAwayTeamNameUI] = useState(
    dataRef.current.awayTeamName
  );
  const [homeScoreUI, setHomeScoreUI] = useState(dataRef.current.homeScore);
  const [awayScoreUI, setAwayScoreUI] = useState(dataRef.current.awayScore);
  const [periodUI, setPeriodUI] = useState(dataRef.current.period);
  const [timeUI, setTimeUI] = useState({
    minute: dataRef.current.periodMinute,
    second: dataRef.current.periodSecond,
  });

  // Animation states
  const [animateHomeScore, setAnimateHomeScore] = useState(false);
  const [animateAwayScore, setAnimateAwayScore] = useState(false);
  const [animatePeriod, setAnimatePeriod] = useState(false);

  // Update throttling
  const updateTimerRef = useRef(null);
  const lastUpdateTimeRef = useRef(Date.now());
  const THROTTLE_PERIOD = 300; // ms

  // Clock countdown references
  const countdownIntervalRef = useRef(null);
  const isClockRunningRef = useRef(false);

  // Function to update UI safely with throttling
  const updateUI = (updates) => {
    console.log("Scheduling UI update with:", updates);

    // Cancel any pending updates
    if (updateTimerRef.current) {
      clearTimeout(updateTimerRef.current);
    }

    const now = Date.now();
    const timeSinceLastUpdate = now - lastUpdateTimeRef.current;

    // If we recently updated, delay this update
    if (timeSinceLastUpdate < THROTTLE_PERIOD) {
      console.log(
        `Throttling update - last update was ${timeSinceLastUpdate}ms ago`
      );
      updateTimerRef.current = setTimeout(() => {
        applyUIUpdates(updates);
      }, THROTTLE_PERIOD - timeSinceLastUpdate);
    } else {
      // Update immediately
      applyUIUpdates(updates);
    }
  };

  // Apply the actual UI updates
  const applyUIUpdates = (updates) => {
    console.log("Applying UI updates:", updates);

    if (updates.homeTeamName !== undefined)
      setHomeTeamNameUI(updates.homeTeamName);
    if (updates.awayTeamName !== undefined)
      setAwayTeamNameUI(updates.awayTeamName);

    if (updates.homeScore !== undefined) {
      setHomeScoreUI(updates.homeScore);
      setAnimateHomeScore(true);
      setTimeout(() => setAnimateHomeScore(false), 300);
    }

    if (updates.awayScore !== undefined) {
      setAwayScoreUI(updates.awayScore);
      setAnimateAwayScore(true);
      setTimeout(() => setAnimateAwayScore(false), 300);
    }

    if (updates.period !== undefined) {
      setPeriodUI(updates.period);
      setAnimatePeriod(true);
      setTimeout(() => setAnimatePeriod(false), 300);
    }

    if (updates.time !== undefined) {
      setTimeUI(updates.time);
    }

    lastUpdateTimeRef.current = Date.now();
    updateTimerRef.current = null;
  };

  // Handle the clock countdown
  useEffect(() => {
    const startCountdown = () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }

      isClockRunningRef.current = true;
      console.log("Starting clock countdown");

      countdownIntervalRef.current = setInterval(() => {
        setTimeUI((prevTime) => {
          // Check if we're already at 0:00
          if (prevTime.minute === 0 && prevTime.second === 0) {
            // Stop the countdown
            clearInterval(countdownIntervalRef.current);
            isClockRunningRef.current = false;
            return prevTime;
          }

          let newMinute = prevTime.minute;
          let newSecond = prevTime.second - 1;

          if (newSecond < 0) {
            newSecond = 59;
            newMinute = Math.max(0, newMinute - 1);
          }

          // Update our data ref to keep it in sync
          dataRef.current.periodMinute = newMinute;
          dataRef.current.periodSecond = newSecond;

          return { minute: newMinute, second: newSecond };
        });
      }, 1000);
    };

    const stopCountdown = () => {
      if (countdownIntervalRef.current) {
        console.log("Stopping clock countdown");
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      isClockRunningRef.current = false;
    };

    // Cleanup function
    return () => {
      stopCountdown();
    };
  }, []);

  // Data fetching happens separately from rendering
  useEffect(() => {
    console.log("Setting up data fetch interval");

    const fetchData = async () => {
      try {
        // Add a timestamp to prevent caching
        const response = await fetch(
          `https://blitzsports.com/api/blitzbox/isilive-1/score?t=${Date.now()}`
        );
        const newData = await response.json();

        console.log("Fetched new data:", newData);

        // Get the reason from the JSON (with default empty string if not provided)
        const reason = newData.reason || "";
        const prevReason = dataRef.current.reason || "";

        // Handle clock based on reason
        if (reason === "Clock Started" && prevReason !== "Clock Started") {
          // Clock just started - use new time values and start countdown
          dataRef.current.periodMinute = newData.periodMinute;
          dataRef.current.periodSecond = newData.periodSecond;

          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
          }

          // Update UI with the new time
          updateUI({
            time: {
              minute: newData.periodMinute,
              second: newData.periodSecond,
            },
          });

          // Start the countdown after a brief delay to ensure the UI is updated
          setTimeout(() => {
            if (dataRef.current.reason === "Clock Started") {
              if (!isClockRunningRef.current) {
                const startCountdown = () => {
                  isClockRunningRef.current = true;
                  console.log("Starting clock countdown");
                  // ... rest of startCountdown logic
                };
                startCountdown();
              }
            }
          }, 100);
        } else if (
          reason === "Clock Stopped" &&
          prevReason !== "Clock Stopped"
        ) {
          // Clock was explicitly stopped - stop countdown and use the provided time values
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
            isClockRunningRef.current = false;
            console.log("Clock explicitly stopped");
          }
        }

        // Save the reason
        dataRef.current.reason = reason;

        // Compare with our current data reference to detect changes
        const updates = {};
        let hasChanges = false;

        if (newData.homeTeamName !== dataRef.current.homeTeamName) {
          updates.homeTeamName = newData.homeTeamName;
          dataRef.current.homeTeamName = newData.homeTeamName;
          hasChanges = true;
        }

        if (newData.awayTeamName !== dataRef.current.awayTeamName) {
          updates.awayTeamName = newData.awayTeamName;
          dataRef.current.awayTeamName = newData.awayTeamName;
          hasChanges = true;
        }

        if (newData.homeScore !== dataRef.current.homeScore) {
          updates.homeScore = newData.homeScore;
          dataRef.current.homeScore = newData.homeScore;
          hasChanges = true;
        }

        if (newData.awayScore !== dataRef.current.awayScore) {
          updates.awayScore = newData.awayScore;
          dataRef.current.awayScore = newData.awayScore;
          hasChanges = true;
        }

        if (newData.period !== dataRef.current.period) {
          updates.period = newData.period;
          dataRef.current.period = newData.period;
          hasChanges = true;
        }

        // Only update time from the JSON if we're not in "Clock Started" mode
        // or if we just entered/exited "Clock Started" mode (handled above)
        if (
          reason !== "Clock Started" &&
          (newData.periodMinute !== dataRef.current.periodMinute ||
            newData.periodSecond !== dataRef.current.periodSecond)
        ) {
          updates.time = {
            minute: newData.periodMinute,
            second: newData.periodSecond,
          };

          dataRef.current.periodMinute = newData.periodMinute;
          dataRef.current.periodSecond = newData.periodSecond;
          hasChanges = true;
        }

        // Only trigger UI updates if something changed (other than time during countdown)
        if (hasChanges) {
          console.log("Changes detected, updating UI");
          updateUI(updates);
        } else {
          console.log("No changes detected");
        }
      } catch (error) {
        console.error("Error fetching score data:", error);
      }
    };

    // Fetch immediately on mount
    fetchData();

    // Set up polling interval - 5 seconds as requested
    const interval = setInterval(fetchData, 5000);

    // Cleanup function
    return () => {
      console.log("Cleaning up data fetch interval");
      clearInterval(interval);
      if (updateTimerRef.current) {
        clearTimeout(updateTimerRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, []); // Empty dependency array - this only runs on mount/unmount

  // The render itself
  console.log("Rendering UI with:", {
    homeScoreUI,
    awayScoreUI,
    periodUI,
    timeUI,
  });

  return (
    <div
      className="h-screen w-full m-0 p-0"
      style={{ backgroundColor: "transparent" }}
    >
      <div className="w-full flex items-start">
        <div className="bg-black bg-opacity-90 text-white p-3 rounded-b-lg shadow-2xl w-full max-w-3xl mx-auto border-2 border-slate-100 portrait:rounded-none">
          <div className="grid grid-cols-11 gap-1 items-center">
            {/* Home Team Logo */}
            <div className="col-span-2 flex justify-center items-center h-24">
              {getTeamLogo(homeTeamNameUI) && (
                <img
                  src={getTeamLogo(homeTeamNameUI)}
                  alt={`${homeTeamNameUI} logo`}
                  className="h-full w-auto object-contain"
                />
              )}
            </div>

            {/* Home Team */}
            <div className="col-span-3 text-center">
              <div className="text-lg md:text-xl font-bold mb-1">
                {homeTeamNameUI}
              </div>
              <div
                className={`text-3xl md:text-5xl font-bold text-blue-400 transition-all duration-300 ${
                  animateHomeScore
                    ? "opacity-70 scale-110"
                    : "opacity-100 scale-100"
                }`}
              >
                {homeScoreUI}
              </div>
            </div>

            {/* Period and Time */}
            <div className="col-span-1 text-center bg-gray-800 rounded-lg py-1.5">
              <div className="text-xs md:text-base font-semibold">Period</div>
              <div
                className={`text-lg md:text-xl font-bold text-yellow-400 transition-all duration-300 ${
                  animatePeriod
                    ? "opacity-70 scale-110"
                    : "opacity-100 scale-100"
                }`}
              >
                {periodUI}
              </div>
              <div className="text-base md:text-lg font-mono">
                {`${timeUI.minute}:${String(timeUI.second).padStart(2, "0")}`}
              </div>
            </div>

            {/* Away Team */}
            <div className="col-span-3 text-center">
              <div className="text-lg md:text-xl font-bold mb-1">
                {awayTeamNameUI}
              </div>
              <div
                className={`text-3xl md:text-5xl font-bold text-red-400 transition-all duration-300 ${
                  animateAwayScore
                    ? "opacity-70 scale-110"
                    : "opacity-100 scale-100"
                }`}
              >
                {awayScoreUI}
              </div>
            </div>

            {/* Away Team Logo */}
            <div className="col-span-2 flex justify-center items-center h-24">
              {getTeamLogo(awayTeamNameUI) && (
                <img
                  src={getTeamLogo(awayTeamNameUI)}
                  alt={`${awayTeamNameUI} logo`}
                  className="h-full w-auto object-contain"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Scoreboard;
